import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api, saveToken } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { Spinner } from "@/components/shared";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import Vehicles from "@/pages/Vehicles";
import WorkOrders from "@/pages/WorkOrders";
import WorkOrderDetail from "@/pages/WorkOrderDetail";
import Queue from "@/pages/Queue";
import Mechanic from "@/pages/Mechanic";
import WarehousePage from "@/pages/Warehouse";
import Inventory from "@/pages/Inventory";
import Purchasing from "@/pages/Purchasing";
import Cashier from "@/pages/Cashier";
import Finance from "@/pages/Finance";
import Payroll from "@/pages/Payroll";
import UsersPage from "@/pages/Users";
import SettingsPage from "@/pages/Settings";
import Marketplace from "@/pages/Marketplace";
import WhatsApp from "@/pages/WhatsApp";
import AIAssistant from "@/pages/AIAssistant";
import AuditLogs from "@/pages/AuditLogs";
import Reports from "@/pages/Reports";
import Tracking from "@/pages/Tracking";
import Bookings from "@/pages/Bookings";
import VehicleHistory from "@/pages/VehicleHistory";
import Landing from "@/pages/Landing";
import Activate from "@/pages/Activate";
import PendingPage from "@/pages/Pending";

function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = new URLSearchParams(location.hash.slice(1)).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId });
        saveToken(data);
        setUser(data);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/app", { replace: true, state: { user: data } });
      } catch {
        navigate("/login?error=1", { replace: true });
      }
    })();
  }, [location.hash, navigate, setUser]);
  return <div className="min-h-screen mjm-bg"><Spinner /></div>;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen mjm-bg"><Spinner /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Home() {
  const { user, can } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "PENDING") return <PendingPage />;
  if (can("dashboard.read")) return <Navigate to="/dashboard" replace />;
  return <Navigate to="/mekanik" replace />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/aktivasi" element={<Activate />} />
      <Route path="/track/:token" element={<Tracking />} />
      <Route path="/tracking/:token" element={<Tracking />} />
      <Route path="/" element={<Landing />} />
      <Route path="/app" element={<Protected><Home /></Protected>} />
      <Route element={<Protected><RoleGate /></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/pelanggan" element={<Customers />} />
        <Route path="/kendaraan" element={<Vehicles />} />
        <Route path="/kendaraan/:id" element={<VehicleHistory />} />
        <Route path="/booking" element={<Bookings />} />
        <Route path="/work-orders" element={<WorkOrders />} />
        <Route path="/work-orders/:id" element={<WorkOrderDetail />} />
        <Route path="/antrean" element={<Queue />} />
        <Route path="/mekanik" element={<Mechanic />} />
        <Route path="/gudang" element={<WarehousePage />} />
        <Route path="/inventori" element={<Inventory />} />
        <Route path="/pembelian" element={<Purchasing />} />
        <Route path="/kasir" element={<Cashier />} />
        <Route path="/keuangan" element={<Finance />} />
        <Route path="/payroll" element={<Payroll />} />
        <Route path="/pengguna" element={<UsersPage />} />
        <Route path="/pengaturan" element={<SettingsPage />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/whatsapp" element={<WhatsApp />} />
        <Route path="/ai" element={<AIAssistant />} />
        <Route path="/audit" element={<AuditLogs />} />
        <Route path="/laporan" element={<Reports />} />
      </Route>
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
}

function RoleGate() {
  const { user } = useAuth();
  if (user.role === "PENDING") return <PendingPage />;
  return <AppLayout />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster theme="dark" richColors position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}
